import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { DateRangeValue } from '@ui';
import { OngoingCloseBadge, FilterLabelRow } from '@ui';
import { getDateLabel, isOngoingPeriod } from './date_range_label';

export interface AnalyticsSummaryWidgetsProps {
  totalUsers: number;
  hwm: number;
  hwmMonth?: string;
  totalMessages: number | null;
  isLoading?: boolean;
  /** Omit on the company drill-down page — org/company already shown in the page header. */
  orgLabel?: string;
  companyLabel?: string;
  dateRange: DateRangeValue | null;
}

const AnalyticsSummaryWidgets: React.FC<AnalyticsSummaryWidgetsProps> = ({
  totalUsers,
  hwm,
  totalMessages,
  isLoading,
  orgLabel,
  companyLabel,
  dateRange,
}) => {
  const dateLabel = getDateLabel(dateRange);
  const ongoing = isOngoingPeriod(dateRange);

  const avgMessages =
    totalMessages !== null && totalUsers > 0
      ? Math.round((totalMessages / totalUsers) * 10) / 10
      : null;

  const filterProps = { orgLabel, companyLabel, dateLabel, ongoing };

  const Skeleton = ({ width = 120 }: { width?: number }) => (
    <span className="asw-skeleton" style={{ width }} aria-hidden="true" />
  );

  // Card footer (prototype `.metric__foot`): filter context on the left,
  // Ongoing/Closed tag on the right, above a hairline.
  const footer = (
    <div className="asw-foot">
      <div className="suc-filter-row asw-foot-context">
        <FilterLabelRow {...filterProps} />
      </div>
      <OngoingCloseBadge isOngoing={ongoing} />
    </div>
  );

  return (
    <div className="asw-grid">
      {/* Total Users */}
      <div className="asw-card">
        <div className="asw-metric">
          <div className="asw-card-label">Total Users</div>
          <div className="asw-card-value asw-card-value--blue" aria-live="polite">
            {isLoading ? <Skeleton /> : totalUsers.toLocaleString()}
          </div>
          <div className="asw-card-subtitle">Provisioned users</div>
        </div>
        {footer}
      </div>

      {/* High-water Mark */}
      <div className="asw-card">
        <div className="asw-metric">
          <div className="asw-card-label">High-water Mark (HWM)</div>
          <div className="asw-card-value asw-card-value--amber" aria-live="polite">
            {isLoading ? <Skeleton /> : hwm.toLocaleString()}
          </div>
          <div className="asw-card-subtitle">Peak provisioned in period</div>
        </div>
        {footer}
      </div>

      {/* Total Messages */}
      <div className="asw-card">
        <div className="asw-metric">
          <div className="asw-card-label">Total Messages</div>
          <div className="asw-figure">
            <div className="asw-card-value asw-card-value--dark" aria-live="polite">
              {isLoading ? (
                <Skeleton />
              ) : totalMessages !== null ? (
                totalMessages.toLocaleString()
              ) : (
                '—'
              )}
            </div>
            {!isLoading && avgMessages !== null && (
              <p className="asw-avg-pill">
                <span className="asw-avg-value">{avgMessages.toLocaleString()}</span>{' '}
                <span className="asw-avg-label">msg / user (avg)</span>
              </p>
            )}
          </div>
          <div className="asw-card-subtitle">Chat messages sent</div>
        </div>
        {footer}
      </div>
    </div>
  );
};

export default AnalyticsSummaryWidgets;
